import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, AlertTriangle, Shield, Zap, Thermometer, Beaker, Settings, Headphones, Edit, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Risks = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    tipo: 'fisico',
    descricao: '',
    setor: '',
    probabilidade: 3,
    severidade: 3,
    medidas_controle: ''
  });

  useEffect(() => {
    fetchRisks();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchRisks();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, []);

  const fetchRisks = async () => {
    try {
      const response = await axios.get(`${API_URL}/risks`);
      setRisks(response.data);
    } catch (error) {
      toast.error('Erro ao carregar riscos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calcula o nível de risco baseado na probabilidade e severidade
      const riskScore = formData.probabilidade * formData.severidade;
      let nivel = 'baixo';
      if (riskScore >= 20) nivel = 'grave';
      else if (riskScore >= 12) nivel = 'alto';
      else if (riskScore >= 6) nivel = 'moderado';
      
      // Prepara os dados no formato esperado pelo backend
      const dataToSend = {
        tipo: formData.tipo,
        descricao: formData.descricao,
        setor: formData.setor,
        nivel: nivel,
        fonte: `Probabilidade: ${formData.probabilidade}, Severidade: ${formData.severidade}`,
        medidas_controle: formData.medidas_controle ? formData.medidas_controle.split('\n').filter(m => m.trim()) : []
      };
      
      await axios.post(`${API_URL}/risks`, dataToSend);
      toast.success('Risco cadastrado com sucesso!');
      setOpen(false);
      setFormData({
        tipo: 'fisico',
        descricao: '',
        setor: '',
        probabilidade: 3,
        severidade: 3,
        medidas_controle: ''
      });
      fetchRisks();
    } catch (error) {
      console.error('Erro:', error.response?.data);
      toast.error('Erro ao cadastrar risco');
    }
  };

  const getRiskColor = (nivel) => {
    const colors = {
      baixo: 'bg-emerald-500 text-white shadow-emerald-500/30',
      moderado: 'bg-yellow-500 text-white shadow-yellow-500/30',
      alto: 'bg-orange-500 text-white shadow-orange-500/30',
      grave: 'bg-red-500 text-white shadow-red-500/30'
    };
    return colors[nivel] || 'bg-gray-500 text-white';
  };
  
  const getRiskCardBg = (nivel) => {
    const colors = {
      baixo: 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100',
      moderado: 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-100',
      alto: 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-100',
      grave: 'bg-gradient-to-br from-red-50 to-rose-50 border-red-100'
    };
    return colors[nivel] || 'bg-white';
  };
  
  const getTypeIcon = (tipo) => {
    const icons = {
      fisico: Thermometer,
      quimico: Beaker,
      biologico: Shield,
      ergonomico: Settings,
      psicossocial: Headphones,
      acidente: Zap
    };
    const Icon = icons[tipo] || AlertTriangle;
    return <Icon size={20} />;
  };

  const handleEditRisk = (risk) => {
    setEditData({
      id: risk.id,
      tipo: risk.tipo,
      descricao: risk.descricao,
      setor: risk.setor,
      nivel: risk.nivel || risk.nivel_risco,
      fonte: risk.fonte || '',
      medidas_controle: Array.isArray(risk.medidas_controle) ? risk.medidas_controle.join('\n') : risk.medidas_controle || ''
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        tipo: editData.tipo,
        descricao: editData.descricao,
        setor: editData.setor,
        nivel: editData.nivel,
        fonte: editData.fonte,
        medidas_controle: editData.medidas_controle ? editData.medidas_controle.split('\n').filter(m => m.trim()) : []
      };
      
      await axios.put(`${API_URL}/risks/${editData.id}`, payload);
      toast.success('Risco atualizado com sucesso!');
      setEditOpen(false);
      fetchRisks();
    } catch (error) {
      console.error('Erro ao atualizar:', error.response?.data);
      toast.error('Erro ao atualizar risco');
    }
  };

  const handleDeleteRisk = async (riskId) => {
    if (!window.confirm('Tem certeza que deseja excluir este risco?')) return;
    try {
      await axios.delete(`${API_URL}/risks/${riskId}`);
      toast.success('Risco excluído com sucesso!');
      fetchRisks();
    } catch (error) {
      toast.error('Erro ao excluir risco');
    }
  };

  const getRiskLabel = (nivel) => {
    const labels = {
      baixo: 'Risco Leve',
      moderado: 'Risco Moderado',
      alto: 'Risco Alto',
      grave: 'Risco Grave/Iminente'
    };
    return labels[nivel] || nivel;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#e8f4fc] rounded-full"></div>
            <div className="w-16 h-16 border-4 border-[#0d9488] border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <span className="text-[#64748b] font-medium">Carregando riscos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="risks-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Identificação de Riscos Ocupacionais
          </h2>
          <p className="text-[#64748b] mt-1">
            Registre e classifique riscos ocupacionais
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#0d9488] hover:bg-[#0b7d73] text-white shadow-lg shadow-[#0d9488]/30"
              data-testid="add-risk-button"
            >
              <Plus size={18} className="mr-2" />
              Novo Risco
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="risk-dialog">
            <DialogHeader>
              <DialogTitle>Cadastrar Risco Ocupacional</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tipo">Tipo de Risco</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger data-testid="risk-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fisico">Físico</SelectItem>
                      <SelectItem value="quimico">Químico</SelectItem>
                      <SelectItem value="biologico">Biológico</SelectItem>
                      <SelectItem value="ergonomico">Ergonômico</SelectItem>
                      <SelectItem value="psicossocial">Psicossocial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="setor">Setor</Label>
                  <Input
                    id="setor"
                    value={formData.setor}
                    onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                    required
                    data-testid="risk-setor-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="descricao">Descrição do Risco</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  rows={3}
                  data-testid="risk-descricao-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="probabilidade">
                    Probabilidade (1-5): {formData.probabilidade}
                  </Label>
                  <Input
                    id="probabilidade"
                    type="range"
                    min="1"
                    max="5"
                    value={formData.probabilidade}
                    onChange={(e) => setFormData({ ...formData, probabilidade: parseInt(e.target.value) })}
                    data-testid="risk-probabilidade-input"
                  />
                </div>
                <div>
                  <Label htmlFor="severidade">
                    Severidade (1-5): {formData.severidade}
                  </Label>
                  <Input
                    id="severidade"
                    type="range"
                    min="1"
                    max="5"
                    value={formData.severidade}
                    onChange={(e) => setFormData({ ...formData, severidade: parseInt(e.target.value) })}
                    data-testid="risk-severidade-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="medidas_controle">Medidas de Controle</Label>
                <Textarea
                  id="medidas_controle"
                  value={formData.medidas_controle}
                  onChange={(e) => setFormData({ ...formData, medidas_controle: e.target.value })}
                  required
                  rows={3}
                  data-testid="risk-medidas-input"
                />
              </div>
              <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Nível de Risco Calculado:</strong> O sistema calcula automaticamente com base
                  em probabilidade × severidade
                </p>
              </div>
              <Button type="submit" className="w-full" data-testid="risk-submit-button">
                Cadastrar Risco
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Risk Matrix Heatmap */}
      <Card className="p-6 rounded-2xl border-0 shadow-[0_4px_20px_rgba(30,58,95,0.05)]" data-testid="risk-matrix">
        <h3 className="text-lg font-bold text-[#1e3a5f] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Matriz de Riscos (5x5)
        </h3>
        <div className="grid grid-cols-6 gap-2 text-center text-sm">
          <div></div>
          <div className="font-bold text-[#64748b]">1</div>
          <div className="font-bold text-[#64748b]">2</div>
          <div className="font-bold text-[#64748b]">3</div>
          <div className="font-bold text-[#64748b]">4</div>
          <div className="font-bold text-[#64748b]">5</div>
          {[5, 4, 3, 2, 1].map((prob) => (
            <React.Fragment key={`row-${prob}`}>
              <div className="font-bold text-[#64748b]">{prob}</div>
              {[1, 2, 3, 4, 5].map((sev) => {
                const score = prob * sev;
                let color = 'bg-emerald-100 text-emerald-700';
                if (score > 20) color = 'bg-red-500 text-white';
                else if (score > 12) color = 'bg-orange-400 text-white';
                else if (score > 6) color = 'bg-yellow-300 text-yellow-800';
                return (
                  <div
                    key={`${prob}-${sev}`}
                    className={`${color} p-4 rounded-xl font-semibold`}
                  >
                    {score}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-[#64748b] mt-4">
          Eixo vertical: Probabilidade | Eixo horizontal: Severidade
        </p>
      </Card>

      {/* Risks List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {risks.map((risk) => (
          <div 
            key={risk.id} 
            className={`${getRiskCardBg(risk.nivel || risk.nivel_risco)} rounded-2xl p-6 border hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}
            data-testid={`risk-card-${risk.id}`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl ${getRiskColor(risk.nivel || risk.nivel_risco)} flex items-center justify-center shadow-lg`}>
                {getTypeIcon(risk.tipo)}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {risk.tipo?.toUpperCase()} - {risk.setor}
                    </h3>
                    <p className="text-sm text-[#64748b] mt-1">
                      {risk.descricao}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-bold ${getRiskColor(risk.nivel || risk.nivel_risco)} shadow-lg`}
                  >
                    {getRiskLabel(risk.nivel || risk.nivel_risco)}
                  </span>
                </div>
                {(risk.probabilidade || risk.fonte) && (
                <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-[#94a3b8]">Fonte:</span>
                    <span className="ml-2 font-bold text-[#1e3a5f]">{risk.fonte || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8]">Nível:</span>
                    <span className="ml-2 font-bold text-[#1e3a5f]">{getRiskLabel(risk.nivel || risk.nivel_risco)}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8]">Medidas:</span>
                    <span className="ml-2 font-bold text-[#1e3a5f]">
                      {risk.medidas_controle?.length || 0}
                    </span>
                  </div>
                </div>
                )}
                {risk.medidas_controle && risk.medidas_controle.length > 0 && (
                <div className="mt-4 bg-white/60 backdrop-blur-sm p-3 rounded-xl">
                  <p className="text-xs font-bold text-[#64748b] mb-1 uppercase tracking-wider">
                    Medidas de Controle:
                  </p>
                  <p className="text-sm text-[#1e3a5f]">
                    {Array.isArray(risk.medidas_controle) ? risk.medidas_controle.join(', ') : risk.medidas_controle}
                  </p>
                </div>
                )}
                
                {/* Botões de Ação */}
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEditRisk(risk)}
                    className="flex-1"
                  >
                    <Edit size={14} className="mr-1" />
                    Editar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDeleteRisk(risk.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {risks.length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center shadow-[0_4px_20px_rgba(30,58,95,0.05)]">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#e8f4fc] flex items-center justify-center mb-4">
            <AlertTriangle size={32} className="text-[#94a3b8]" />
          </div>
          <p className="text-[#64748b]">
            Nenhum risco cadastrado ainda. Clique em "Novo Risco" para começar.
          </p>
        </div>
      )}

      {/* Modal de Edição de Risco */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Risco Ocupacional</DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-4">
              <div>
                <Label>Tipo de Risco</Label>
                <Select value={editData.tipo} onValueChange={(v) => setEditData({ ...editData, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fisico">Físico</SelectItem>
                    <SelectItem value="quimico">Químico</SelectItem>
                    <SelectItem value="biologico">Biológico</SelectItem>
                    <SelectItem value="ergonomico">Ergonômico</SelectItem>
                    <SelectItem value="psicossocial">Psicossocial</SelectItem>
                    <SelectItem value="acidente">Acidente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={editData.descricao}
                  onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Setor</Label>
                  <Input
                    value={editData.setor}
                    onChange={(e) => setEditData({ ...editData, setor: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nível de Risco</Label>
                  <Select value={editData.nivel} onValueChange={(v) => setEditData({ ...editData, nivel: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixo">Baixo</SelectItem>
                      <SelectItem value="moderado">Moderado</SelectItem>
                      <SelectItem value="alto">Alto</SelectItem>
                      <SelectItem value="grave">Grave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Fonte do Risco</Label>
                <Input
                  value={editData.fonte}
                  onChange={(e) => setEditData({ ...editData, fonte: e.target.value })}
                  placeholder="Ex: Equipamento, Ambiente, Processo..."
                />
              </div>
              <div>
                <Label>Medidas de Controle (uma por linha)</Label>
                <Textarea
                  value={editData.medidas_controle}
                  onChange={(e) => setEditData({ ...editData, medidas_controle: e.target.value })}
                  rows={3}
                  placeholder="Ex: Uso de EPI&#10;Treinamento periódico&#10;Sinalização adequada"
                />
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
                <Button onClick={handleSaveEdit}>Salvar Alterações</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Risks;
